// Lightweight i18n layer (no external dependency).
//
// - LanguageProvider holds the active language ('en' | 'it'), auto-detected on
//   first load from a persisted choice or the browser locale, and persisted to
//   localStorage when changed.
// - useT() returns t(key, vars?) which looks up the active locale, falls back to
//   English, then to the key itself — so a missing Italian string shows the
//   English text, never blank.
// - useLang() returns { lang, setLang } for the flag switcher.
//
// Plain JS + React.createElement (no JSX) to match theme.js and avoid adding a
// .jsx/.tsx surface. This is UI text only — it does NOT touch the voice
// assistant's own Italian/English handling.

import { createContext, useContext, useState, useCallback, useMemo, createElement } from 'react';
import en from './en';
import it from './it';

const LOCALES = { en, it };
export const LANGS = ['en', 'it'];
const STORAGE_KEY = 'floxir.lang';

// Persisted choice wins; otherwise an Italian browser → 'it', else 'en'.
function detectLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'it') return saved;
  } catch (_) {}
  try {
    const navs = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || 'en'];
    if (navs.some((l) => String(l).toLowerCase().startsWith('it'))) return 'it';
  } catch (_) {}
  return 'en';
}

const LanguageContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  const setLang = useCallback((next) => {
    if (next !== 'en' && next !== 'it') return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {}
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = LOCALES[lang] || en;
      let s = dict && dict[key] != null ? dict[key] : en[key] != null ? en[key] : key;
      if (vars) {
        for (const k in vars) s = String(s).split('{' + k + '}').join(String(vars[k]));
      }
      return s;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return createElement(LanguageContext.Provider, { value }, children);
}

export function useLang() {
  const { lang, setLang } = useContext(LanguageContext);
  return { lang, setLang };
}

export function useT() {
  return useContext(LanguageContext).t;
}
